import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Modal, ModalDialog, ModalOverflow, Stack, Tooltip, Typography } from "@mui/joy";
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import { ensureDateObject, formatDate, formatDateForDatabase, isValidDate } from "@utils/dates";
import ObjectPreview from "./objects/ObjectPreview";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FieldEditTable } from "./FieldEditTable";
import { useUpdateTask } from "@api/taskService";
import toast from "react-hot-toast";
import { queryClient } from "@api/queryClient";
import { authenticatedApi } from "api";
import { useAuth } from "@providers/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateWorkPlan } from "@api/workplanService";
import { addDays, differenceInDays } from "date-fns";

const fields = [
    {
        name: 'wpinfo',
        label: false,
        type: 'info',
        content: 'Work Plan Information'
    },
    {
        name: 'workplan_id',
        label: 'Work Plan',
        type: 'relationship',
        endpoint: 'work-plans',
        // defaultProp: 'owner',
        conditional: object => !object?.has_workplan,
        showRequired: true,
        config: {
            required: true,
        }
    },

    {
        name: 'name',
        type: 'text',
        placeholder: 'e.g. 2024 Clean Up Day',
        showRequired: true,
        config: {
            required: true,
        }
    },
    {name: 'workplan_date', label: 'Date', type: 'date', placeholder: 'e.g. 05/23/2016'},
];

const cloneOptionFields = [
    {
        name: 'cloneinfo',
        label: false,
        type: 'info',
        content: 'Cloning Options'
    },
    {
        name: 'contacts',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy attached contacts',
    },
    {
        name: 'involvements',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy attached involvements',
    },
    {
        name: 'tasks',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy attached tasks',
    },

    /*
    {
        name: 'actual_to_estimate',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy old actual budget to new estimated budget',
        conditional: object => !!object?.tasks,
    },
    {
        name: 'keep_budget',
        label: false,
        type: 'boolean',
        inputLabel: 'Keep estimated budget only',
        conditional: object => !!object?.tasks,
    },
    {
        name: 'clear_budget',
        label: false,
        type: 'boolean',
        inputLabel: 'Don\'t copy budget',
        conditional: object => !!object?.tasks,
    },
    */
    {
        name: 'copy_budget',
        label: false,
        type: 'select',
        placeholder: 'Choose budget type...',
        conditional: object => !!object?.tasks,
        options: [
            {label: 'Copy old actual budget to new estimated budget', value: 'actual_to_estimate'},
            {label: 'Keep estimated budget only', value: 'keep_budget'},
            {label: 'Don\'t copy budget', value: 'clear_budget'},
        ],
    },
    /*
    Copy old actual budget to new estimated budget
    Keep estimated budget only

    */

    {
        name: 'tasks_assignments',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy task assignments',
        conditional: object => !!object?.tasks,
    },
    {
        name: 'tasks_dates',
        label: false,
        type: 'boolean',
        inputLabel: 'Backdate tasks from work plan date',
        conditional: object => !!object?.tasks,
    },

];

export function WorkPlanCloneModal({workplan = null, open, setOpen}) {
    const navigate = useNavigate();

    const [saving, setSaving] = useState(false);

    const mutation = useCreateWorkPlan();

    let defaultValues = {
        name: '',
        workplan_date: null,

        copy_budget: 'actual_to_estimate',
        /*
        actual_to_estimate: true,
        keep_budget: false,
        clear_budget: false,
        */
        contacts: true,
        involvements: true,
        tasks: true,
        tasks_assignments: true,
        tasks_dates: true,
    };

    if(workplan) {
        defaultValues.has_workplan = true;
        defaultValues.workplan_id = workplan.id;
    }

    const formMethods = useForm({
        defaultValues,
    });
    const {
        setValue,
        reset,
        handleSubmit,
        watch,
        getValues,
        formState: { isSubmitting, errors, isValid, isDirty }
    } = formMethods;

    // without this, the work plan wont get identified correctly
    useEffect(() => {
        reset(defaultValues);
    }, [workplan]);


    /*
    const actual_to_estimate_watch = watch('actual_to_estimate');
    const keep_budget_watch = watch('keep_budget');
    const clear_budget_watch = watch('clear_budget');

    useEffect(() => {
      setValue('keep_budget', false);

      if(!actual_to_estimate_watch) {
            setValue('clear_budget', true);
        } else {
            setValue('clear_budget', false);
        }
    }, [actual_to_estimate_watch]);

    useEffect(() => {
        setValue('actual_to_estimate', false);

        if(!keep_budget_watch) {
            setValue('clear_budget', true);
        } else {
            setValue('clear_budget', false);
        }
    }, [keep_budget_watch]);

    useEffect(() => {
        setValue('keep_budget', false);

        if(!clear_budget_watch) {
            setValue('actual_to_estimate', true);
        } else {
            setValue('actual_to_estimate', false);
        }
    }, [clear_budget_watch]);
    */

    const onSubmit = (fields) => {
        asyncSubmit(fields);
    };

    const asyncSubmit = async (fields) => {
        if(saving) {
            return;
        }

        setSaving(true);

        let newWorkPlan = {
            name: fields.name,
            workplan_date: fields.workplan_date,
        };

        // get work plan
        const previousWorkPlan = await authenticatedApi.get(`/work-plans/${fields.workplan_id}?include=all`);

        if(fields.contacts) {
            newWorkPlan.contacts = previousWorkPlan.data.contacts.map(obj => {
                return {
                    id: obj.id,
                    pivot: {
                        value: obj.pivot.value,
                    },
                };
            });
        }

        if(fields.involvements) {
            newWorkPlan.involvements = previousWorkPlan.data.involvements.map(obj => {
                return {
                    id: obj.id,
                    pivot: {
                        notes: obj.pivot.notes,
                    },
                };
            });
        }

        if(fields.tasks) {
            newWorkPlan.tasks = previousWorkPlan.data.tasks.map(obj => {
                let task = {
                    task_name: obj.task_name,
                    actual_budget: obj.actual_budget,
                    budget_type: obj.budget_type,
                    notes: obj.notes,
                };

                if(fields.copy_budget === 'actual_to_estimate') {
                    task.estimated_budget = obj.actual_budget;
                }
                if(fields.copy_budget === 'keep_budget') {
                    task.estimated_budget = obj.estimated_budget;
                }
                if(fields.copy_budget === 'clear_budget') {
                    task.estimated_budget = 0;
                }

                if(
                    fields.tasks_dates
                    && obj.due_date
                    && newWorkPlan.workplan_date
                    && previousWorkPlan.data.workplan_date
                ) {
                    let oldWpDate = ensureDateObject(previousWorkPlan.data.workplan_date);
                    let oldTaskDate = ensureDateObject(obj.due_date);

                    let diff = differenceInDays(oldTaskDate, oldWpDate);

                    let newWpDate = ensureDateObject(newWorkPlan.workplan_date);
                    let newTaskDate = addDays(newWpDate, diff);

                    task.due_date = formatDateForDatabase(newTaskDate);
                }

                if(fields.tasks_assignments) {
                    task.contacts = obj.contacts.map(contact => ({id: contact.id}));
                }

                return task;
            });
        } else {
            newWorkPlan.tasks = [];
        }

        newWorkPlan.event_chair_id = previousWorkPlan.data.event_chair_id;
        newWorkPlan.description = previousWorkPlan.data.description;
        newWorkPlan.clone = true;

        const performMutation = mutation.mutateAsync({
            ...newWorkPlan,
        });

        toast.promise(performMutation, {
            loading: `Cloning...`,
            success: `The work plan was cloned!`,
            error: (err) => `There was an error while cloning.`,
        });

        performMutation.then((response) => {
            setOpen(false);
            navigate(`/work-plans/${response.id}`);
        }).finally(() => {
            setSaving(false);
        });

    }

    return (
    <Modal open={open} onClose={() => saving ? null : setOpen(false)}>
        <ModalOverflow>
        <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
            <ModalDialog sx={{width: '100%', maxWidth: '800px'}}>
                <DialogTitle>
                    {!!workplan && `Clone ${workplan.name}`}
                    {!workplan && `Clone Work Plan`}
                    {/* {task ? <>Add Time to <em>{task.task_name}</em></> : 'Add Time'} */}
                </DialogTitle>
                <Divider/>
                <Box sx={{
                    marginInline: 'var(--Divider-inset)',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2
                }}
                >
                    <Box sx={{flex: '1 1 50%'}}>
                    <FieldEditTable mode="row" fields={fields}/>
                    </Box>
                    <Box sx={{flex: '1 1 50%', /*borderLeft: 1, borderLeftColor: 'var(--joy-palette-divider)'*/}}>
                    <FieldEditTable mode="row" fields={cloneOptionFields}/>
                    </Box>
                </Box>
                <DialogActions>
                    <Button
                        disabled={!isValid}
                        loading={saving}
                        type="button" // this can't be submit type or it will submit the parent form
                        onClick={() => {
                        onSubmit(getValues());
                        }}
                    >
                        Clone
                    </Button>
                    <Button
                        disabled={saving}
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                            setOpen(false);
                            try {
                                // onCancel();
                            } catch(e) {
                                //
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </ModalDialog>
        </FormProvider>
        </form>
        </ModalOverflow>
    </Modal>
    )
}
